import React, { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, Trash2, UserX } from 'lucide-react';
import { DataTable, type ColumnDef } from '../../components/ui/DataTable';
import { PageHeader } from '../../components/ui/PageHeader';
import { toast } from 'sonner';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../api/client';
import type { Client, BulkResult, PaginatedResponse } from '../../api/types';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Drawer } from '../../components/ui/Drawer';
import { Modal, ConfirmDialog } from '../../components/ui/Modal';
import { EventStatusBadge } from '../../components/ui/Badge';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonRow } from '../../components/ui/Skeleton';
import { BulkActionBar } from '../../components/ui/BulkActionBar';
import { RowActionsMenu } from '../../components/ui/RowActionsMenu';
import { useTableSelection } from '../../hooks/useTableSelection';
import { formatDate, formatCurrency, clientFullName } from '../../utils/format';
import { InfoSection, InfoRow } from '../../components/ui/InfoCard';
import { useNavigate } from 'react-router-dom';

import { useDebounce } from '../../hooks/useDebounce';

type ClientSortField = 'name' | 'email' | 'city' | 'events';
type SortOrder = 'asc' | 'desc';

function sortClients(clients: import('../../api/types').Client[], field: ClientSortField, order: SortOrder) {
  return [...clients].sort((a, b) => {
    let cmp = 0;
    switch (field) {
      case 'name':   cmp = `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`); break;
      case 'email':  cmp = (a.email ?? '').localeCompare(b.email ?? ''); break;
      case 'city':   cmp = (a.city ?? '').localeCompare(b.city ?? ''); break;
      case 'events': cmp = (a._count?.events ?? 0) - (b._count?.events ?? 0); break;
    }
    return order === 'asc' ? cmp : -cmp;
  });
}

export default function ClientsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);
  const [page, setPage] = useState(1);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState<Partial<Client>>({});
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [bulkArchiveOpen, setBulkArchiveOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const [sortBy, setSortBy] = useState<ClientSortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  const resetKey = `${debouncedSearch}|${page}`;
  const sel = useTableSelection(resetKey);

  const handleSort = (field: ClientSortField) => {
    if (sortBy === field) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortOrder('asc'); }
  };

  const { data, isLoading } = useQuery<PaginatedResponse<Client>>({
    queryKey: ['clients', debouncedSearch, page],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (debouncedSearch) params.set('search', debouncedSearch);
      return await apiGet<PaginatedResponse<Client>>(`/clients?${params}`);
    },
  });

  const rawClients = data?.data ?? [];
  const clients = useMemo(() => sortClients(rawClients, sortBy, sortOrder), [rawClients, sortBy, sortOrder]);


  const invalidate = () => qc.invalidateQueries({ queryKey: ['clients'] });

  const createMutation = useMutation({
    mutationFn: (body: Partial<Client>) => apiPost<Client>('/clients', body),
    onSuccess: () => {
      invalidate();
      setShowNewModal(false);
      setForm({});
      toast.success('Cliente creato');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...body }: Partial<Client> & { id: string }) =>
      apiPatch<Client>(`/clients/${id}`, body),
    onSuccess: (updated) => {
      invalidate();
      setSelectedClient(updated);
      setEditMode(false);
      toast.success('Cliente aggiornato');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/clients/${id}`),
    onSuccess: (_, id) => {
      invalidate();
      if (selectedClient?.id === id) setSelectedClient(null);
      setDeleteTarget(null);
      sel.clearSelection();
      toast.success('Cliente eliminato');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const bulkArchiveMutation = useMutation({
    mutationFn: (ids: string[]) => apiPost<BulkResult>('/clients/bulk/archive', { ids }),
    onSuccess: (res) => {
      invalidate(); sel.clearSelection(); setBulkArchiveOpen(false);
      toast.success(`${res.updated} clienti archiviati`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => apiPost<BulkResult>('/clients/bulk/delete', { ids }),
    onSuccess: (res) => {
      invalidate(); sel.clearSelection(); setBulkDeleteOpen(false);
      toast.success(`${res.updated} clienti eliminati`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const openDrawer = useCallback(async (client: Client) => {
    const detail = await apiGet<Client>(`/clients/${client.id}`);
    setSelectedClient(detail);
    setEditMode(false);
  }, []);

  const handleCreate = (e: React.FormEvent) => { e.preventDefault(); createMutation.mutate(form); };
  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClient) return;
    updateMutation.mutate({ id: selectedClient.id, ...form });
  };

  const selectedIds = [...sel.selectedIds];

  const clientColumns: ColumnDef<Client>[] = [
    {
      key: 'icon', label: '',
      render: (c) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }} onClick={e => e.stopPropagation()}>
          <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
            {c.firstName?.[0]}{c.lastName?.[0]}
          </div>
        </div>
      ),
    },
    {
      key: 'name', label: 'Cliente', sortable: true,
      render: (c) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.firstName} {c.lastName}</div>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.email || '—'}</div>
        </div>
      ),
    },
    {
      key: 'phone', label: 'Telefono',
      render: (c) => <span style={{ whiteSpace: 'nowrap' }}>{c.phone || '—'}</span>,
    },
    {
      key: 'city', label: 'Città', sortable: true,
      render: (c) => <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.city || '—'}</span>,
    },
    {
      key: 'events', label: 'Eventi', sortable: true, type: 'number',
      render: (c) => (
        <span style={{
          background: 'rgba(255,255,255,0.06)', borderRadius: 'var(--radius-full)',
          padding: '0.125rem 0.5rem', fontSize: 'var(--text-xs)', fontWeight: 600,
          color: 'var(--color-text-muted)', textAlign: 'center',
        }}>
          {c._count?.events ?? 0}
        </span>
      ),
    },
    {
      key: 'actions', label: '',
      render: (c) => (
        <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
          <RowActionsMenu actions={[
            { label: 'Apri', onClick: () => openDrawer(c) },
            { label: 'Elimina', icon: <Trash2 size={14} />, onClick: () => setDeleteTarget(c.id), variant: 'danger' },
          ]} />
        </div>
      ),
    },
  ];

  return (
    <div className="page-enter" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <PageHeader
        title="Clienti"
        subtitle={`${data?.meta.total ?? 0} clienti totali`}
        actions={[
          { label: 'Nuovo cliente', icon: <Plus size={16} />, onClick: () => { setForm({}); setShowNewModal(true); }, variant: 'primary' },
        ]}
      />

      <div style={{ position: 'relative', maxWidth: 420 }}>
        <Search size={15} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-faint)' }} />
        <input
          type="search"
          placeholder="Cerca per nome, email, città…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          style={{
            background: 'var(--glass-bg-card)',
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 'var(--radius-card)',
            color: 'var(--color-text)',
            fontSize: 'var(--text-sm)',
            padding: '0.5rem 0.75rem 0.5rem 2.25rem',
            width: '100%',
            outline: 'none',
          }}
        />
      </div>

      <BulkActionBar
        count={sel.selectedCount}
        onClear={sel.clearSelection}
        actions={[
          { label: 'Archivia', icon: <UserX size={14} />, onClick: () => setBulkArchiveOpen(true), variant: 'secondary', loading: bulkArchiveMutation.isPending },
          { label: 'Elimina', icon: <Trash2 size={14} />, onClick: () => setBulkDeleteOpen(true), variant: 'danger', loading: bulkDeleteMutation.isPending },
        ]}
      />

      {isLoading ? (
        Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
      ) : (
      <DataTable
        columns={clientColumns}
        data={clients}
        gridColumns="68px 2fr 1.5fr 1fr 0.5fr 44px"
        rowKey={(c) => c.id}
        onRowClick={(c) => openDrawer(c)}
        emptyState={
          <EmptyState
            icon={<Search size={32} />}
            title="Nessun cliente trovato"
            description={search ? 'Prova con un termine diverso' : 'Crea il tuo primo cliente'}
            action={<Button onClick={() => setShowNewModal(true)} size="sm">Nuovo cliente</Button>}
          />
        }
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={(field) => handleSort(field as ClientSortField)}
        selectable
        selectedIds={sel.selectedIds}
        onSelectionChange={(ids) => sel.setSelectedIds(ids)}
        isLoading={isLoading}
        stickyHeader
      />
      )}
      {data && data.meta.pages > 1 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
          padding: '0.875rem',
        }}>
          <Button variant="ghost" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>←</Button>
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            Pagina {page} di {data.meta.pages}
          </span>
          <Button variant="ghost" size="sm" disabled={page >= data.meta.pages} onClick={() => setPage(p => p + 1)}>→</Button>
        </div>
      )}

      {/* Detail Drawer */}
      <Drawer
        open={!!selectedClient}
        onClose={() => { setSelectedClient(null); setEditMode(false); }}
        title={selectedClient ? clientFullName(selectedClient) : ''}
        width={460}
        footer={editMode ? (
          <>
            <Button variant="ghost" onClick={() => setEditMode(false)}>Annulla</Button>
            <Button onClick={handleUpdate} loading={updateMutation.isPending}>Salva</Button>
          </>
        ) : (
          <div style={{ display: 'flex', gap: '0.5rem', width: '100%', justifyContent: 'space-between' }}>
            <Button
              variant="danger"
              size="sm"
              onClick={() => selectedClient && setDeleteTarget(selectedClient.id)}
            >
              Elimina
            </Button>
            <Button variant="secondary" onClick={() => { setForm({ firstName: selectedClient?.firstName ?? '', lastName: selectedClient?.lastName ?? '', email: selectedClient?.email ?? '', phone: selectedClient?.phone ?? '', address: selectedClient?.address ?? '', city: selectedClient?.city ?? '', birthDate: selectedClient?.birthDate ? selectedClient.birthDate.split('T')[0] : '', birthPlace: selectedClient?.birthPlace ?? '', fiscalCode: selectedClient?.fiscalCode ?? '', vatNumber: selectedClient?.vatNumber ?? '', notes: selectedClient?.notes ?? '' }); setEditMode(true); }}>
              Modifica
            </Button>
          </div>
        )}
      >
        {selectedClient && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {editMode ? (
              <form onSubmit={handleUpdate} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <Input label="Nome" value={form.firstName ?? ''} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} required />
                  <Input label="Cognome" value={form.lastName ?? ''} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} required />
                </div>
                <Input label="Email" type="email" value={form.email ?? ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                <Input label="Numero Cellulare" value={form.phone ?? ''} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                <Input label="Indirizzo" value={form.address ?? ''} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
                <Input label="Città" value={form.city ?? ''} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
                <Input label="Data di Nascita" type="date" value={form.birthDate ? form.birthDate.split('T')[0] : ''} onChange={e => setForm(f => ({ ...f, birthDate: e.target.value }))} />
                <Input label="Luogo di Nascita" value={form.birthPlace ?? ''} onChange={e => setForm(f => ({ ...f, birthPlace: e.target.value }))} />
                <Input label="Codice fiscale" value={form.fiscalCode ?? ''} onChange={e => setForm(f => ({ ...f, fiscalCode: e.target.value }))} />
                <Input label="P. IVA" value={form.vatNumber ?? ''} onChange={e => setForm(f => ({ ...f, vatNumber: e.target.value }))} />
              </form>
            ) : (
              <>
                <InfoSection title="Anagrafica">
                  <InfoRow label="Email" value={selectedClient.email} />
                  <InfoRow label="Numero Cellulare" value={selectedClient.phone} />
                  <InfoRow label="Indirizzo" value={selectedClient.address} />
                  <InfoRow label="Città" value={selectedClient.city} />
                  <InfoRow label="Data di Nascita" value={selectedClient.birthDate ? new Date(selectedClient.birthDate).toLocaleDateString('it-IT') : undefined} />
                  <InfoRow label="Luogo di Nascita" value={selectedClient.birthPlace} />
                  <InfoRow label="Codice fiscale" value={selectedClient.fiscalCode} />
                  <InfoRow label="P. IVA" value={selectedClient.vatNumber} />
                  <InfoRow label="Cliente dal" value={formatDate(selectedClient.createdAt)} />
                </InfoSection>

                {selectedClient.notes && (
                  <InfoSection title="Note">
                    <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
                      {selectedClient.notes}
                    </p>
                  </InfoSection>
                )}

                {selectedClient.events && selectedClient.events.length > 0 && (
                  <InfoSection title={`Eventi (${selectedClient.events.length})`}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {selectedClient.events.map(ev => (
                        <button
                          key={ev.id}
                          onClick={() => { navigate(`/events/${ev.id}`); setSelectedClient(null); }}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '0.625rem 0.75rem',
                            background: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.10)',
                            borderRadius: 'var(--radius-card)',
                            cursor: 'pointer',
                            transition: 'all var(--transition-interactive)',
                            width: '100%', textAlign: 'left',
                          }}
                          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
                          onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.04)')}
                        >
                          <div>
                            <p style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text)' }}>{ev.title}</p>
                            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                              {formatDate(ev.eventDate)} · {formatCurrency(ev.totalAmount)}
                            </p>
                          </div>
                          <EventStatusBadge status={ev.status} />
                        </button>
                      ))}
                    </div>
                  </InfoSection>
                )}
              </>
            )}
          </div>
        )}
      </Drawer>

      {/* New Client Modal */}
      <Modal
        open={showNewModal}
        onClose={() => setShowNewModal(false)}
        title="Nuovo cliente"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowNewModal(false)}>Annulla</Button>
            <Button onClick={handleCreate} loading={createMutation.isPending}>Crea cliente</Button>
          </>
        }
      >
        <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input label="Nome *" value={form.firstName ?? ''} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} required />
            <Input label="Cognome *" value={form.lastName ?? ''} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} required />
          </div>
          <Input label="Email" type="email" value={form.email ?? ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          <Input label="Numero Cellulare" value={form.phone ?? ''} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
          <Input label="Indirizzo" value={form.address ?? ''} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
          <Input label="Città" value={form.city ?? ''} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
          <Input label="Data di Nascita" type="date" value={form.birthDate ? form.birthDate.split('T')[0] : ''} onChange={e => setForm(f => ({ ...f, birthDate: e.target.value }))} />
          <Input label="Luogo di Nascita" value={form.birthPlace ?? ''} onChange={e => setForm(f => ({ ...f, birthPlace: e.target.value }))} />
          <Input label="Codice fiscale" value={form.fiscalCode ?? ''} onChange={e => setForm(f => ({ ...f, fiscalCode: e.target.value }))} />
          <Input label="P. IVA" value={form.vatNumber ?? ''} onChange={e => setForm(f => ({ ...f, vatNumber: e.target.value }))} />
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget)}
        title="Elimina cliente"
        description="Il cliente verrà eliminato. Gli eventi e i progetti associati rimarranno nel sistema ma non saranno più collegati a un cliente attivo."
        confirmLabel="Elimina"
        loading={deleteMutation.isPending}
      />

      <ConfirmDialog
        open={bulkArchiveOpen}
        onClose={() => setBulkArchiveOpen(false)}
        onConfirm={() => bulkArchiveMutation.mutate(selectedIds)}
        title="Archivia clienti"
        description={`Archiviare ${sel.selectedCount} clienti selezionati? Verranno disattivati ma i loro dati resteranno nel sistema.`}
        confirmLabel="Archivia"
        loading={bulkArchiveMutation.isPending}
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={() => bulkDeleteMutation.mutate(selectedIds)}
        title="Elimina clienti"
        description={`Eliminare ${sel.selectedCount} clienti selezionati? I dati verranno disattivati e non saranno più visibili nell'elenco.`}
        confirmLabel="Elimina"
        requiresConfirmCheck
        loading={bulkDeleteMutation.isPending}
      />
    </div>
  );
}
